import React, { forwardRef, useCallback, useImperativeHandle, useState } from 'react';
import type { Dispatch, MouseEvent, SetStateAction } from 'react';

import { TooltipPositions } from '../../components/tooltips/tooltipConstants';
import useScrollOnListItemFocus from '../../components/lists/useScrollOnListItemFocus';
import FloatingPanel from '../../components/tooltips/FloatingPanel';
import PopupPanelMouseManager from '../../components/popupPanel/PopupPanelMouseManager';
import Button from '../../components/buttons/Button';
import Icon from '../../components/icons/Icon';
import { isEmpty } from 'lodash';
import useListFocusManager from '../../components/lists/useListFocusManager';
import { hyperFormulaList } from './manager/hyperFormulaInstance';
import { PopupIds } from '../../components/popupPanel/popupConstants';
import { useTranslator } from '../../components/i18n/useTranslator';

import './FormulaDropdown.scss';

export type FormulaDropdownRef = {
    updateFormulaDropdown: (show: boolean, cellContent?: string) => boolean;
    useFocusedFormula: () => void;
    moveListFocusUp: () => void;
    moveListFocusDown: () => void;
    isFormulaDropdownOpen: () => boolean;
};

export type FormulaDropdownProps = {
    addFormulaToCell: (formula: string) => void;
    showFormulaDropdown: boolean;
    setShowFormulaDropdown: Dispatch<SetStateAction<boolean>>;
};

type FormulaListItemProps = FormulaDropdownProps & {
    currentContent: string;
    index: number;
    formula: string;
    focusedIndex: number;
};

export const getMatchingFormulas = (currentCellContent: string, allFormulas: string[]) => {
    const query = currentCellContent.toUpperCase().replace('=', '');
    if (!query) return ['SUM', 'IF', 'MIN', 'MAX', 'COUNT', 'AVERAGE'];

    return allFormulas.filter((formula) => formula.startsWith(query));
};

const FormulaListItem: React.FC<FormulaListItemProps> = ({
    currentContent,
    index,
    formula,
    focusedIndex,
    addFormulaToCell,
    setShowFormulaDropdown,
}) => {
    const [focused, listItemRef] = useScrollOnListItemFocus({ focusedIndex, index });

    const searchQuery = currentContent.toUpperCase().replace('=', '');
    const restOfFormula = formula.substring(searchQuery.length);

    const onClick = (event: MouseEvent) => {
        addFormulaToCell(formula);
        setShowFormulaDropdown(false);

        event.stopPropagation();
        event.preventDefault();
    };

    return (
        <li className={`formula-option ${focused}`} key={index} tabIndex={0} ref={listItemRef}>
            <Button onClick={onClick}>
                {!!searchQuery && (
                    <>
                        <strong>{searchQuery}</strong>
                        {restOfFormula}
                    </>
                )}
                {!searchQuery && <>{formula}</>}
            </Button>
        </li>
    );
};

const FormulaDropdown = forwardRef<FormulaDropdownRef, FormulaDropdownProps>(function FormulaDropdownContent(
    { addFormulaToCell, showFormulaDropdown, setShowFormulaDropdown },
    ref,
) {
    const [formulaDropdownData, setFormulaDropdownData] = useState<string[]>(hyperFormulaList);
    const [currentContent, setCurrentContent] = useState('');

    const focusManager = useListFocusManager({
        loop: true,
        initialFocusIndex: 0,
        listSize: formulaDropdownData.length,
    });
    const { focusedIndex, setFocusIndex, moveListFocusUp, moveListFocusDown } = focusManager;

    const useFocusedFormula = useCallback(() => {
        const formula = formulaDropdownData[focusedIndex];
        if (!formula) return;

        addFormulaToCell(formula);
    }, [addFormulaToCell, formulaDropdownData, focusedIndex]);

    const translate = useTranslator('popup', 'table.formula');

    /**
     * Returns false when formula dropdown is not shown, for example when no formula matches the current cell content.
     */
    const updateFormulaDropdown = useCallback(
        (show: boolean, cellContent?: string) => {
            setShowFormulaDropdown(show);

            if (cellContent === currentContent) return formulaDropdownData.length > 0;

            setFocusIndex(0);
            setCurrentContent(cellContent ?? '');

            if (!cellContent) return false;

            const newFormulaList = getMatchingFormulas(cellContent, hyperFormulaList);
            setFormulaDropdownData(newFormulaList);

            return newFormulaList.length > 0;
        },
        [currentContent, formulaDropdownData.length, setFocusIndex, setShowFormulaDropdown],
    );

    const showDropdown = showFormulaDropdown && !isEmpty(formulaDropdownData);

    useImperativeHandle(
        ref,
        () => ({
            updateFormulaDropdown,
            useFocusedFormula,
            moveListFocusUp,
            moveListFocusDown,
            isFormulaDropdownOpen: () => showDropdown,
        }),
        [moveListFocusDown, moveListFocusUp, showDropdown, updateFormulaDropdown, useFocusedFormula],
    );

    return (
        <>
            {showDropdown && (
                <FloatingPanel
                    arrowColor="var(--ui-modal-background-primary)"
                    arrowStrokeColor="var(--ui-modal-background-primary-hover)"
                    className="FormulaDropdown"
                    selector=".Table.selected .handsontable-input-holder"
                    position={TooltipPositions.BOTTOM}
                    pollPosition
                >
                    <PopupPanelMouseManager
                        addFormulaToCell={addFormulaToCell}
                        showFormulaDropdown={showFormulaDropdown}
                        setShowFormulaDropdown={setShowFormulaDropdown}
                        visible={showDropdown}
                        popupId={PopupIds.FORMULA_DROPDOWN}
                        closePopup={() => setShowFormulaDropdown(false)}
                    >
                        <ul className="formula-list" data-table-util={true}>
                            {formulaDropdownData.map((formula, index) => (
                                <FormulaListItem
                                    formula={formula}
                                    key={index}
                                    index={index}
                                    currentContent={currentContent}
                                    focusedIndex={focusedIndex}
                                    addFormulaToCell={addFormulaToCell}
                                    showFormulaDropdown={showFormulaDropdown}
                                    setShowFormulaDropdown={setShowFormulaDropdown}
                                />
                            ))}
                        </ul>
                        <div className="formula-docs-link">
                            <Button
                                onMouseDown={() => {
                                    const newWindow = window.open(
                                        'https://help.milanote.com/en/articles/8584809-formulas-functions-for-tables',
                                        '_blank',
                                        'noopener,noreferrer',
                                    );

                                    if (newWindow) {
                                        newWindow.opener = null;
                                    }
                                }}
                            >
                                <span>{translate('helpLink')}</span>
                                <Icon name="chevron-right" />
                            </Button>
                        </div>
                    </PopupPanelMouseManager>
                </FloatingPanel>
            )}
        </>
    );
});

export default FormulaDropdown;
